// pages/_app.js
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import CssBaseline from "@mui/material/CssBaseline";
import Slide from "@mui/material/Slide";
import { ThemeProvider } from "@mui/material/styles";
import { LicenseManager } from "ag-grid-enterprise";
import Router from "next/router";
import Script from "next/script";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { SnackbarProvider } from "notistack";
import { useEffect, useState } from "react";
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";
import { getEnv } from "utils/env";
import AuthGuard from "../components/AuthGuard";
import { UserProfileProvider } from "../components/base-components/MagicBell/UserProfileContext";
import { store } from "../store";
import theme from "../theme/theme-config";

import "../styles/tailwind.css";
import "../theme/main.scss";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../theme/table-styles.scss";

LicenseManager.setLicenseKey(
  "Using_this_{AG_Grid}_Enterprise_key_{AG-108420}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Caltana,_Inc.}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Caltana}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Caltana}_need_to_be_licensed___{Caltana}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{20_September_2027}____[v3]_[01]_MTgyMTM5NDgwMDAwMA==fc128c9922583da1f2127e403cd4c6cc",
);

function isAutomatedTestBrowser() {
  return typeof navigator !== "undefined" && navigator.webdriver === true;
}

// Datadog RUM configuration definition: https://docs.datadoghq.com/real_user_monitoring/browser/
// Disable datadog for local development environments
if (
  (process.env.NEXT_PUBLIC_VERCEL_ENV === "preview" ||
    process.env.NEXT_PUBLIC_VERCEL_ENV === "production") &&
  !isAutomatedTestBrowser()
) {
  try {
    datadogLogs.init({
      clientToken: "pub991a3cacb1eba9538678833aec0ac23b",
      site: "datadoghq.com",
      service: "web-legacy",
      env: getEnv(),
      version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
      forwardConsoleLogs: ["error", "warn"],
      forwardErrorsToLogs: true,
      forwardReports: "all",
      sessionSampleRate: 100,
      storeContextsAcrossPages: true,
      trackSessionAcrossSubdomains: true,
      useSecureSessionCookie: true,
      silentMultipleInit: true,
    });
    datadogRum.init({
      applicationId: "c9568ff5-acac-4874-83da-b7cd4248f23b",
      clientToken: "pub991a3cacb1eba9538678833aec0ac23b",
      site: "datadoghq.com",
      service: "web-legacy",
      env: getEnv(),
      version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
      sampleRate: 100,
      sessionReplaySampleRate: 100,
      trackInteractions: true,
      trackFrustrations: true,
      trackResources: true,
      trackLongTasks: true,
      trackSessionAcrossSubdomains: true,
      useSecureSessionCookie: true,
      defaultPrivacyLevel: "mask-user-input",
      silentMultipleInit: true,
      allowedTracingUrls: [
        {
          match: "https://app-api.caltana.com",
          propagatorTypes: ["tracecontext", "datadog"],
        },
        {
          match: "https://dev-api.caltana.com",
          propagatorTypes: ["tracecontext", "datadog"],
        },
      ],
    });
    datadogRum.startSessionReplayRecording();
  } catch (error) {
    console.error("Failed to initialize Datadog:", error);
  }
}

export default function MyApp(props) {
  const { Component, pageProps } = props;
  const getLayout = Component.getLayout || ((page) => page);
  const isUnprotected = Component.isUnprotected || false;
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    const handleStart = () => setIsNavigating(true);
    const handleComplete = () => setIsNavigating(false);

    Router.events.on("routeChangeStart", handleStart);
    Router.events.on("routeChangeComplete", handleComplete);
    Router.events.on("routeChangeError", handleComplete);

    return () => {
      Router.events.off("routeChangeStart", handleStart);
      Router.events.off("routeChangeComplete", handleComplete);
      Router.events.off("routeChangeError", handleComplete);
    };
  }, []);

  return (
    <Provider store={store}>
      <NextThemesProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
      >
        <ThemeProvider theme={theme}>
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            TransitionComponent={Slide}
            hideIconVariant={false}
            dense={false}
          >
            <CssBaseline />
            <div
              id="app-content"
              aria-busy={isNavigating || undefined}
              data-testid="app-content"
            >
              {/* the pages which have isUnprotected(true) can be accessed without any credentials */}
              {isUnprotected ? (
                getLayout(<Component {...pageProps} />)
              ) : (
                <AuthGuard>
                  {(profileState) => (
                    <UserProfileProvider {...profileState}>
                      {getLayout(<Component {...pageProps} />)}
                    </UserProfileProvider>
                  )}
                </AuthGuard>
              )}
            </div>
            <ToastContainer autoClose={1000} />
          </SnackbarProvider>
        </ThemeProvider>
      </NextThemesProvider>

      <Script
        // https://nextjs.org/docs/basic-features/script#afterinteractive
        id="pendo-script"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
          (function(apiKey){
            (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
            v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
                o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
                y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
                z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
          })('00a1de14-5365-4d26-4e11-1a111461d37e');
        `,
        }}
      />

      <Script
        // Caltana Help Center Zendesk widget
        // https://nextjs.org/docs/basic-features/script#lazyonload
        strategy="lazyOnload"
        src="https://static.zdassets.com/ekr/snippet.js?key=3ddfa314-91a5-47e6-8e54-2d1c4dcc8a50"
        id="ze-snippet"
      />
    </Provider>
  );
}
